import React from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Paper,
    Typography,
    Box
} from '@mui/material';

export interface GutAtAGlanceItem {
    [key: string]: string | number;
    Gut_at_a_Glance: string;
}

interface GutAtAGlanceEditorProps {
    data: GutAtAGlanceItem[];
    onChange: (index: number, field: string, value: string | number) => void;
}

const GutAtAGlanceEditor: React.FC<GutAtAGlanceEditorProps> = ({ data, onChange }) => {
    // Ensure data is an array before processing
    const displayData = Array.isArray(data) ? data : [];
    
    if (displayData.length === 0) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">No gut at a glance data available</Typography>
            </Paper>
        );
    }

    // Safely get all unique dynamic field names (excluding 'Gut_at_a_Glance')
    const dynamicFields = Array.from(
        new Set(displayData.flatMap(item => {
            if (!item || typeof item !== 'object') return [];
            return Object.keys(item).filter(key => key !== 'Gut_at_a_Glance');
        }))
    );

    return (
        <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Metric</TableCell>
                        {dynamicFields.map((field, i) => (
                            <TableCell key={i}>{field}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayData.map((item, index) => {
                        // Ensure item is an object with the required properties
                        const gutAtAGlance = item?.Gut_at_a_Glance || '';
                        
                        return (
                            <TableRow key={index}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={gutAtAGlance}
                                        onChange={(e) => onChange(index, 'Gut_at_a_Glance', e.target.value)}
                                    />
                                </TableCell>
                                {dynamicFields.map((field, i) => {
                                    const value = item?.[field] ?? '';
                                    const isNumber = typeof value === 'number';
                                    
                                    return (
                                        <TableCell key={i}>
                                            <TextField
                                                type={isNumber ? 'number' : 'text'}
                                                size="small"
                                                fullWidth
                                                value={value}
                                                onChange={(e) => {
                                                    const newValue = isNumber 
                                                        ? parseFloat(e.target.value) || 0 
                                                        : e.target.value;
                                                    onChange(index, field, newValue);
                                                }}
                                                inputProps={isNumber ? { step: '0.01' } : {}}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default GutAtAGlanceEditor;
